import { db } from '../services/db';
import { processVoiceCommand } from './voiceUtils';

export interface UrlParams {
  action?: 'clock-in' | 'clock-out' | 'switch';
  project?: string;
  task?: string;
  location?: string;
  notes?: string;
  fromVoice?: boolean;
}

export const parseUrlParams = (): UrlParams => {
  if (!window.location.search) return {};

  const params = new URLSearchParams(window.location.search);
  const textValue =
    params.get('text') ||
    params.get('command') ||
    params.get('voiceCommand') ||
    params.get('input') ||
    params.get('shortcutInput');
  const voiceFlag = params.get('voice');
  const isVoiceShortcut =
    voiceFlag !== null && ['true', '1', 'yes'].includes(voiceFlag.toLowerCase());
  
  // Check if this is a voice command payload or a Siri shortcut wrapper.
  if (textValue) {
    return {
      fromVoice: true,
      action: (params.get('action') as UrlParams['action']) || undefined,
      project: params.get('project') || undefined,
      task: params.get('task') || undefined,
      location: params.get('location') || undefined,
      notes: params.get('notes') || textValue
    };
  }

  if (isVoiceShortcut) {
    return {
      fromVoice: true,
      action: (params.get('action') as UrlParams['action']) || undefined,
      project: params.get('project') || undefined,
      task: params.get('task') || undefined,
      location: params.get('location') || undefined,
      notes: params.get('notes') || undefined
    };
  }
  
  // Standard parameter parsing
  return {
    action: (params.get('action') as UrlParams['action']) || undefined,
    project: params.get('project') || undefined,
    task: params.get('task') || undefined,
    location: params.get('location') || undefined,
    notes: params.get('notes') || undefined,
    fromVoice: false
  };
};

/**
 * Interface for pending voice commands that need manual confirmation
 */
export interface PendingVoiceCommand {
  id?: number;
  timestamp: Date;
  originalText: string;
  action?: 'clock-in' | 'clock-out' | 'switch';
  project?: string;
  task?: string;
  location?: string;
  notes?: string;
  confidence: number;
  processed: boolean;
}

/**
 * Saves a voice command for manual confirmation later
 */
export const saveVoiceCommandForConfirmation = async (command: PendingVoiceCommand): Promise<void> => {
  try {
    // Add timestamp and processed flag
    const pendingCommand = {
      ...command,
      timestamp: new Date(),
      processed: false
    };
    
    // Save to IndexedDB
    await db.pendingVoiceCommands.add(pendingCommand);
    
    // Show notification if supported
    if ('Notification' in window && Notification.permission === 'granted') {
      // Create a notification that can be clicked to open the PWA
      const notification = new Notification('DayTrakr Lite', {
        body: 'Voice command received. Tap to review in the app.',
        icon: './logo192.png',
        badge: './logo192.png',
        tag: 'voice-command'
        // Note: renotify is not supported in all browsers, removed to fix TypeScript error
      } as NotificationOptions);
      
      // Add click handler to open the PWA with the voice commands modal
      notification.onclick = function() {
        // Try to focus on the PWA window if it exists
        if (window.parent !== window) {
          window.parent.focus();
        } else {
          window.focus();
        }
        
        // Navigate to the app with a special parameter to open the voice commands modal
        const baseUrl = `${window.location.origin}${window.location.pathname}`;
        window.location.href = `${baseUrl}?openVoiceCommands=true`;
        
        // Close the notification
        notification.close();
      };
    }
  } catch (error) {
    console.error('Error saving voice command:', error);
  }
};

/**
 * Gets all pending voice commands that need confirmation
 */
export const getPendingVoiceCommands = async (): Promise<PendingVoiceCommand[]> => {
  try {
    return await db.pendingVoiceCommands
      .filter(command => !command.processed)
      .toArray();
  } catch (error) {
    console.error('Error getting pending voice commands:', error);
    return [];
  }
};

/**
 * Marks a voice command as processed
 */
export const markVoiceCommandAsProcessed = async (id: number): Promise<void> => {
  try {
    await db.pendingVoiceCommands.update(id, { processed: true });
  } catch (error) {
    console.error('Error marking voice command as processed:', error);
  }
};

export const processUrlParams = async (): Promise<void> => {
  const params = parseUrlParams();
  
  // Process voice command if present
  if (params.fromVoice && params.notes) {
    const voiceResult = await processVoiceCommand(params.notes);
    
    // If confidence is low or critical fields are missing, save for manual confirmation
    if (voiceResult.needsConfirmation) {
      await saveVoiceCommandForConfirmation({
        ...voiceResult,
        timestamp: new Date(),
        processed: false,
        confidence: voiceResult.confidence
      });
      
      // Clear the URL parameters after processing
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }
    
    // Otherwise, use the processed parameters
    Object.assign(params, voiceResult);
  }
  
  if (!params.action) return;
  
  try {
    const currentEntry = await db.getCurrentTimeEntry();
    
    switch (params.action) {
      case 'clock-in':
        if (!currentEntry) {
          // Find project in database or create one if it doesn't exist
          if (params.project) {
            const projects = await db.projects.where('name').equals(params.project).toArray();
            if (projects.length === 0) {
              await db.projects.add({
                name: params.project,
                tasks: params.task ? [params.task] : []
              });
            } else if (params.task && !projects[0].tasks.includes(params.task)) {
              await db.projects.update(projects[0].id!, {
                tasks: [...projects[0].tasks, params.task]
              });
            }
          }

          const userName = await db.getUserName();
          
          // Create a new time entry using the new addTimeEntry method
          await db.addTimeEntry({
            name: userName || 'Anonymous',
            timeIn: new Date(),
            project: params.project || 'Default Project',
            task: params.task,
            location: params.location || 'Site',
            notes: params.notes || ''
          });
        }
        break;
      
      case 'clock-out':
        if (currentEntry) {
          await db.updateTimeEntry(currentEntry.id!, {
            timeOut: new Date(),
            notes: params.notes ? `${currentEntry.notes || ''} ${params.notes}`.trim() : currentEntry.notes || ''
          });
        }
        break;
      
      case 'switch':
        if (currentEntry) {
          // Clock out current entry using the new updateTimeEntry method
          await db.updateTimeEntry(currentEntry.id!, {
            timeOut: new Date(),
            notes: params.notes ? `${currentEntry.notes || ''} ${params.notes}`.trim() : currentEntry.notes || ''
          });
          
          // Find project in database or create one if it doesn't exist
          if (params.project) {
            const projects = await db.projects.where('name').equals(params.project).toArray();
            if (projects.length === 0) {
              await db.projects.add({
                name: params.project,
                tasks: params.task ? [params.task] : []
              });
            } else if (params.task && !projects[0].tasks.includes(params.task)) {
              await db.projects.update(projects[0].id!, {
                tasks: [...projects[0].tasks, params.task]
              });
            }
          }

          // Create a new entry using the new addTimeEntry method
          await db.addTimeEntry({
            name: currentEntry.name,
            timeIn: new Date(),
            project: params.project || currentEntry.project,
            task: params.task || currentEntry.task,
            location: params.location || currentEntry.location || 'Site',
            notes: params.notes || ''
          });
        }
        break;
    }
    
    // Clear the URL parameters after processing
    window.history.replaceState({}, document.title, window.location.pathname);
  } catch (error) {
    console.error('Error processing URL parameters:', error);
  }
};
